import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { BookOpen, FileText, Video, Download, ExternalLink, ArrowLeft, BookMarked, GraduationCap } from 'lucide-react';

export default function Resources() {
  const navigate = useNavigate();

  const resourceCategories = [
    {
      title: "Enneagram Kitapları",
      icon: BookMarked,
      color: "from-blue-500 to-blue-600",
      resources: [
        {
          name: "Enneagram: Eşini Keşfet",
          author: "İsmail Acarkan",
          type: "Kitap",
          description: "Enneagram sistemini Türkçe olarak anlatan temel kaynaklardan biri.",
          link: "https://www.amazon.com.tr/Enneagram-E%C5%9Fini-Ke%C5%9Ffet-%C4%B0smail-Acarkan/dp/6050845395",
          external: true
        },
        {
          name: "The Wisdom of the Enneagram",
          author: "Don Richard Riso & Russ Hudson",
          type: "Kitap",
          description: "Enneagram'ın en kapsamlı kaynaklarından biri. (İngilizce)",
          link: "https://www.enneagraminstitute.com",
          external: true
        }
      ]
    },
    {
      title: "Eğitim Materyalleri",
      icon: GraduationCap,
      color: "from-emerald-500 to-emerald-600",
      resources: [
        {
          name: "Mizaç Tipleri Rehberi",
          author: "Mizaç360 Ekibi",
          type: "PDF",
          description: "9 mizaç tipinin özelliklerini ve eğitim stratejilerini içeren rehber.",
          link: "#",
          external: false,
          comingSoon: true
        },
        {
          name: "Öğretmenler için Uygulama Kılavuzu",
          author: "Mizaç360 Ekibi",
          type: "PDF",
          description: "Sınıf içinde mizaç bilgisini kullanmak için pratik öneriler.",
          link: "#",
          external: false,
          comingSoon: true
        }
      ]
    },
    {
      title: "Video İçerikler",
      icon: Video,
      color: "from-purple-500 to-purple-600",
      resources: [
        {
          name: "Enneagram'a Giriş",
          author: "Eduen.com.tr",
          type: "Video",
          description: "Enneagram sisteminin temellerini anlatan video serisi.",
          link: "https://www.eduen.com.tr",
          external: true
        },
        {
          name: "Mizaç Tiplerini Tanıma",
          author: "Mizaç360 Ekibi",
          type: "Video",
          description: "Her bir mizaç tipinin özelliklerini anlatan eğitim videoları.",
          link: "#",
          external: false,
          comingSoon: true
        }
      ]
    },
    {
      title: "Akademik Kaynaklar",
      icon: FileText,
      color: "from-amber-500 to-amber-600",
      resources: [
        {
          name: "Enneagram Institute Araştırmaları",
          author: "The Enneagram Institute",
          type: "Araştırma",
          description: "Enneagram'ın bilimsel temellerine dair akademik çalışmalar.",
          link: "https://www.enneagraminstitute.com",
          external: true
        },
        {
          name: "Integrative9 Yayınları",
          author: "Integrative9",
          type: "Araştırma",
          description: "Enneagram'ın bütünleştirici yaklaşımına dair makaleler.",
          link: "https://www.integrative9.com/enneagram/",
          external: true
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-emerald-50">
      {/* Header */}
      <header className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-emerald-600 rounded-xl flex items-center justify-center cursor-pointer" onClick={() => navigate('/')}>
              <BookOpen className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-xl font-bold text-slate-800 cursor-pointer" onClick={() => navigate('/')}>Mizaç360</h1>
          </div>
          <div className="flex items-center gap-6">
            <a href="/resources" className="text-slate-700 hover:text-blue-600 font-medium transition">Kaynaklar</a>
            <a href="/blog" className="text-slate-600 hover:text-blue-600 transition">Blog</a>
            <a href="/news" className="text-slate-600 hover:text-blue-600 transition">Haberler & Etkinlikler</a>
            <a href="/about" className="text-slate-600 hover:text-blue-600 transition">Hakkımızda</a>
            <Button variant="outline" onClick={() => navigate('/')}>Ana Sayfa</Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-12">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <Badge className="mb-4 bg-blue-100 text-blue-700">Öğrenme Kaynakları</Badge>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-800 mb-4">
            Kaynak Kütüphanesi
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Enneagram ve mizaç tipleri hakkında daha fazla bilgi edinmek için seçilmiş kaynaklar
          </p>
        </div>

        {/* Resource Categories */}
        <div className="space-y-12">
          {resourceCategories.map((category, idx) => (
            <div key={idx}>
              <div className="flex items-center gap-3 mb-6">
                <div className={`w-12 h-12 bg-gradient-to-br ${category.color} rounded-xl flex items-center justify-center`}>
                  <category.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-slate-800">{category.title}</h3>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {category.resources.map((resource, ridx) => (
                  <Card key={ridx} className="shadow-lg border-none hover:shadow-xl transition-shadow">
                    <CardHeader>
                      <div className="flex justify-between items-start">
                        <div>
                          <CardTitle className="text-lg mb-1">{resource.name}</CardTitle>
                          <CardDescription className="text-sm">{resource.author}</CardDescription>
                        </div>
                        <Badge variant="outline" className="text-xs">{resource.type}</Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-slate-600 text-sm mb-4">{resource.description}</p>
                      {resource.comingSoon ? (
                        <Button variant="outline" disabled className="w-full">
                          Yakında
                        </Button>
                      ) : resource.external ? (
                        <a href={resource.link} target="_blank" rel="noopener noreferrer" className="block">
                          <Button variant="outline" className="w-full group">
                            Kaynağa Git
                            <ExternalLink className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                          </Button>
                        </a>
                      ) : (
                        <Button variant="outline" className="w-full group">
                          İndir
                          <Download className="w-4 h-4 ml-2 group-hover:translate-y-1 transition-transform" />
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* CTA Section */}
        <Card className="mt-16 shadow-xl border-none bg-gradient-to-r from-blue-600 to-emerald-600 text-white">
          <CardContent className="py-12 text-center">
            <h3 className="text-3xl font-bold mb-4">Daha Fazla Kaynak mı Arıyorsunuz?</h3>
            <p className="text-lg mb-8 text-blue-50">
              Blog sayfamızda mizaç tipleri ve eğitim stratejileri hakkında makaleler bulabilirsiniz
            </p>
            <div className="flex gap-4 justify-center">
              <Button 
                size="lg" 
                variant="secondary"
                className="bg-white text-blue-600 hover:bg-blue-50"
                onClick={() => navigate('/blog')}
              >
                Blog'a Git
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                className="border-white text-white hover:bg-white/10"
                onClick={() => navigate('/about')}
              >
                Hakkımızda
              </Button>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Footer */}
      <footer className="bg-slate-800 text-white py-8 mt-16">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-slate-300">© 2025 Mizaç360. Tüm hakları saklıdır.</p>
          <p className="text-sm text-slate-400 mt-2">
            Bilimsel kaynaklar: İsmail Acarkan, Ziya Selçuk, Eduen.com.tr, The Enneagram Institute, Integrative9
          </p>
        </div>
      </footer>
    </div>
  );
}
